// remark-directive互換のリーフディレクティブ（`::name{k="v"}`）を1行単位で、
// テキストディレクティブ（文中の `:name{k="v"}`）を行の中から解析する。
// 属性値はダブルクォート必須とし、エスケープは扱わない。

export interface MarkdownDirective {
  name: string;
  attributes: Record<string, string>;
}

const DIRECTIVE_LINE_PATTERN = /^::([a-z][a-z0-9-]*)(?:\{(.*)\})?$/;
const LEADING_ATTRIBUTE_PATTERN = /^\s*([a-z][a-z0-9-]*)="([^"]*)"/;
const TEXT_DIRECTIVE_PATTERN = /(?<![:\w]):([a-z][a-z0-9-]*)\{([^{}]*)\}/g;
const CONTAINER_DIRECTIVE_START_PATTERN = /^:::([a-z][a-z0-9-]*)(?:\s+(.*))?$/;

export interface ContainerDirectiveStart {
  name: string;
  label: string;
}

// remark-directive互換のコンテナディレクティブ（`:::name ラベル` 〜 `:::`）の開始行を解析する。
export function parseContainerDirectiveStart(
  line: string,
): ContainerDirectiveStart | null {
  const match = CONTAINER_DIRECTIVE_START_PATTERN.exec(line);
  if (match === null) {
    return null;
  }

  const [, name, labelSource] = match;
  return { name, label: (labelSource ?? "").trim() };
}

export function isContainerDirectiveEnd(line: string): boolean {
  return line.trim() === ":::";
}

export function parseDirectiveLine(line: string): MarkdownDirective | null {
  const match = DIRECTIVE_LINE_PATTERN.exec(line);
  if (match === null) {
    return null;
  }

  const [, name, attributesSource] = match;
  const attributes =
    attributesSource === undefined ? {} : parseAttributes(attributesSource);
  if (attributes === null) {
    return null;
  }

  return { name, attributes };
}

export function extractDirectives(
  markdown: string,
  name: string,
): MarkdownDirective[] {
  return markdown
    .split(/\r?\n/)
    .map(parseDirectiveLine)
    .filter(
      (directive): directive is MarkdownDirective =>
        directive !== null && directive.name === name,
    );
}

export interface TextDirectiveMatch {
  directive: MarkdownDirective;
  index: number;
  length: number;
}

export function findTextDirectives(line: string): TextDirectiveMatch[] {
  const matches: TextDirectiveMatch[] = [];
  for (const match of line.matchAll(TEXT_DIRECTIVE_PATTERN)) {
    const [full, name, attributesSource] = match;
    const attributes = parseAttributes(attributesSource);
    if (attributes !== null) {
      matches.push({
        directive: { name, attributes },
        index: match.index,
        length: full.length,
      });
    }
  }
  return matches;
}

export function extractTextDirectives(
  markdown: string,
  name: string,
): MarkdownDirective[] {
  return markdown
    .split(/\r?\n/)
    .filter((line) => parseDirectiveLine(line) === null)
    .flatMap((line) => findTextDirectives(line))
    .map((match) => match.directive)
    .filter((directive) => directive.name === name);
}

function parseAttributes(source: string): Record<string, string> | null {
  const attributes: Record<string, string> = {};
  let remaining = source;

  while (remaining.trim() !== "") {
    const match = LEADING_ATTRIBUTE_PATTERN.exec(remaining);
    if (match === null) {
      return null;
    }
    const [full, key, value] = match;
    attributes[key] = value;
    remaining = remaining.slice(full.length);
  }

  return attributes;
}
